import React, { useEffect, useState } from 'react';
import Paginations from '../common/paginations';
import moment from 'moment';
import constant from '../../constant';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import customAxios from "../../customHook/customAxios";

import { encodeBase64JsonPayload, getAuthHeader } from '../../utils';
import { expenseCurrencySymbolMap } from '../../expenseCurrency';
import {
    createTypedReceiptBlob,
    getExpenseProofUrl,
    getReceiptContentType,
    getReceiptFileExtension,
    receiptImageExtensions,
} from './receiptPreview';

const SubmittedExpensesList = ({ data, setCurrentPage, limit, isAdmin, onDelete }) => {
    const { expenses, pagination } = data;
    const { total, page } = pagination;
    const [previewUrl, setPreviewUrl] = useState(null);
    const [receiptPreview, setReceiptPreview] = useState({
        url: null,
        fileName: '',
        contentType: '',
        error: '',
    });

    const downloadProofFile = async (fileName) => {
        try {
            const response = await customAxios.get(
                getExpenseProofUrl(fileName),
                {
                    ...getAuthHeader(),
                    responseType: 'blob',
                }
            );

            const blob = response.data;
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = getFileNameFromHeaders(response?.headers, fileName);
            a.click();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading file:", err);
            alert("Unable to download the file.");
        }
    };

    const viewProofFile = async (fileName) => {
        try {
            const response = await customAxios.get(
                getExpenseProofUrl(fileName),
                {
                    ...getAuthHeader(),
                    responseType: 'blob',
                }
            );

            const blob = response.data;
            const contentType = getReceiptContentType(
                fileName,
                response?.headers?.['content-type'] || response?.headers?.['Content-Type'],
                blob?.type
            );

            if (receiptPreview?.url) {
                window.URL.revokeObjectURL(receiptPreview.url);
            }

            if (!blob || blob.size === 0) {
                setReceiptPreview({
                    url: null,
                    fileName,
                    contentType,
                    error: 'Receipt file is empty. Please re-upload it.',
                });

                const modal = new window.bootstrap.Modal(
                    document.getElementById("receiptPreviewModal")
                );
                modal.show();
                return;
            }

            const fileUrl = window.URL.createObjectURL(createTypedReceiptBlob(blob, contentType));

            setReceiptPreview({
                url: fileUrl,
                fileName,
                contentType,
                error: '',
            });

            const modal = new window.bootstrap.Modal(
                document.getElementById("receiptPreviewModal")
            );
            modal.show();
        } catch (err) {
            console.error("Error previewing receipt:", err);
            alert("Unable to preview the receipt.");
        }
    };

    const getFileNameFromHeaders = (headers, fallback) => {
        const header = headers?.['content-disposition'] || headers?.['Content-Disposition'];
        if (!header) return fallback;
        const match = header.match(/filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i);
        if (!match) return fallback;
        try {
            return decodeURIComponent(match[1]);
        } catch (err) {
            return match[1];
        }
    };

    // ✅ Export file as PDF/DOCX
    const downloadFile = async (expenseId, type) => {
        try {
            const response = await customAxios.get(
                `${process.env.REACT_APP_BASE_URL}/v3/expense/download/${type}/${expenseId}`,
                {
                    ...getAuthHeader(),
                    responseType: "blob",
                }
            );

            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = getFileNameFromHeaders(
                response?.headers,
                `expense_${expenseId}.${type}`
            );
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error downloading file:", err);
            alert("Unable to download the file.");
        }
    };

    // ✅ Preview PDF inline
    const previewExpense = async (expenseId) => {
        try {
            const response = await customAxios.get(
                `${process.env.REACT_APP_BASE_URL}/v3/expense/download/pdf/${expenseId}`,
                {
                    ...getAuthHeader(),
                    responseType: "blob",
                }
            );
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            if (previewUrl) {
                window.URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(url);
            const modal = new window.bootstrap.Modal(
                document.getElementById("previewModal")
            );
            modal.show();
        } catch (err) {
            console.error("Preview failed:", err);
            alert("Unable to preview the expense report.");
        }
    };

    useEffect(() => () => {
        if (previewUrl) {
            window.URL.revokeObjectURL(previewUrl);
        }
    }, [previewUrl]);

    useEffect(() => () => {
        if (receiptPreview?.url) {
            window.URL.revokeObjectURL(receiptPreview.url);
        }
    }, [receiptPreview?.url]);

    const receiptExtension = getReceiptFileExtension(receiptPreview.fileName);
    const receiptContentType = String(receiptPreview.contentType || '').split(';')[0].toLowerCase();
    const canPreviewPdf = !!receiptPreview.url && (receiptContentType.includes('pdf') || receiptExtension === 'pdf');
    const canPreviewImage = !!receiptPreview.url && (receiptContentType.startsWith('image/') || receiptImageExtensions.includes(receiptExtension));

    const formatExpenseDateRange = (item) => {
        const fromDate = item.EXPENSE_FROM_DATE || item.EXPENSE_DATE;
        const toDate = item.EXPENSE_TO_DATE || item.EXPENSE_DATE;
        if (!fromDate && !toDate) return '-';
        if (fromDate && toDate && moment(fromDate).isSame(toDate, 'day')) {
            return moment(fromDate).format('DD-MM-YYYY');
        }
        const fromText = fromDate ? moment(fromDate).format('DD-MM-YYYY') : '-';
        const toText = toDate ? moment(toDate).format('DD-MM-YYYY') : '-';
        return `${fromText} - ${toText}`;
    };

    const formatLastUpdated = (item) => {
        const timestamp = item?.MODIFIED_ON || item?.CREATED_ON;
        if (!timestamp) return '-';
        const parsed = moment(timestamp);
        return parsed.isValid() ? parsed.format('DD MMM YYYY [at] hh:mm A') : '-';
    };

    return (
        <React.Fragment>

            <div className="table-responsive">
                <table className="table align-middle table-hover border">
                    <thead className="table-light">
                        <tr>
                            <th scope="col" style={{ width: 70 }}>ID</th>
                            <th scope="col">Employee Information</th>
                            <th scope="col">Expense Details</th>
                            {/* <th scope="col">Expense Items</th> */}
                            <th scope="col">Expense Period</th>
                            <th scope="col">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {expenses?.length > 0 ? (
                            expenses.map((item) => (
                                <React.Fragment key={item.ID}>
                                    <tr key={item.ID}>
                                        <td style={{ color: '#2b5a7e', fontSize: 15, fontFamily: 'nunito' }}>
                                            {item.ID}
                                        </td>
                                        <td>
                                            <h5 className="font-size-14 mb-1">
                                                <a href="javascript:void(0)" className="text-dark">{item.FULL_NAME}</a>
                                            </h5>
                                            <p className="text-muted mb-0" style={{ color: '#00add2 !important' }}>
                                                Site Location : <span style={{ color: "#ab5858" }}>{item.LOCATION_NAME}</span>
                                            </p>
                                        </td>
                                        <td>
                                            <strong className="text-dark d-block mb-1">{item.EXPENSE_TYPE_NAME}</strong>
                                            <p className="mb-0">Total Expense :<span style={{ color: "#ab5858" }}> ${item.TOTAL_USD_EQUIV}</span> </p>
                                            <p className="text-muted mb-0">
                                                Status :{" "}
                                                <span
                                                    style={{
                                                        color:
                                                            item.STATUS === 'APPROVED'
                                                                ? 'green'
                                                                : item.STATUS === 'REJECTED'
                                                                    ? 'red'
                                                                    : '#ff7b09',
                                                    }}
                                                >
                                                    {item.STATUS}
                                                </span>
                                            </p>
                                            <p className="text-muted mb-0">
                                                <span style={{ color: '#f46a6a' }}>Last Updated</span> |{" "}
                                                {formatLastUpdated(item)}
                                            </p>
                                        </td>

                                        <td>
                                            <div style={{ cursor: 'pointer' }}>
                                                {formatExpenseDateRange(item)}
                                            </div>
                                        </td>
                                        <td>
                                            <ul className="list-inline font-size-18 mb-0 d-flex align-items-center">
                                                {isAdmin && <li className="list-inline-item me-3">
                                                    <Link
                                                        to={{
                                                            pathname: constant.Authedcomponent.editExpense.url,
                                                            state: {
                                                                data: encodeBase64JsonPayload(item),
                                                                page,
                                                            },
                                                        }}
                                                        title="Edit"
                                                    >
                                                        <i className="bx bx-edit text-primary" />
                                                    </Link>
                                                </li>}
                                                {isAdmin && <li className="list-inline-item me-2">
                                                    <button type="button" className="btn btn-link p-0 text-danger" title="Delete" onClick={() => onDelete(item.ID)}>
                                                        <i className="bx bx-trash" />
                                                    </button>
                                                </li>}

                                                {/* 🔍 View Details */}
                                                <li className="list-inline-item me-2">
                                                    <a
                                                        href="#"
                                                        title="View Details"
                                                        data-bs-toggle="modal"
                                                        data-bs-target={`#expenseModal-${item.ID}`}
                                                    >
                                                        <i className="bx bxs-zoom-in"></i>
                                                    </a>
                                                </li>

                                                {/* 📤 Export Dropdown */}
                                                <li className="list-inline-item">
                                                    <div className="dropdown">
                                                        <button
                                                            className="btn btn-outline-primary btn-sm dropdown-toggle"
                                                            type="button"
                                                            id={`exportDropdown-${item.ID}`}
                                                            data-bs-toggle="dropdown"
                                                            aria-expanded="false"
                                                        >
                                                            <i className="bx bx-export fs-4"></i>
                                                        </button>
                                                        <ul
                                                            className="dropdown-menu"
                                                            aria-labelledby={`exportDropdown-${item.ID}`}
                                                        >
                                                            <li>
                                                                <button
                                                                    className="dropdown-item"
                                                                    onClick={() => downloadFile(item.ID, "pdf")}
                                                                >
                                                                    Download PDF
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button
                                                                    className="dropdown-item"
                                                                    onClick={() => downloadFile(item.ID, "docx")}
                                                                >
                                                                    Download DOCX
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button
                                                                    className="dropdown-item text-info"
                                                                    onClick={() => previewExpense(item.ID)}
                                                                >
                                                                    Preview
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </li>
                                            </ul>
                                        </td>
                                    </tr>
                                    {/* Modal */}
                                    <div
                                        className="modal fade"
                                        id={`expenseModal-${item.ID}`}
                                        data-bs-backdrop="static"
                                        data-bs-keyboard="false"
                                        tabIndex={-1}
                                        role="dialog"
                                        aria-labelledby="expenseModalLabel"
                                        aria-hidden="true"
                                    >
                                        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                                            <div className="modal-content">
                                                <div className="modal-header">
                                                    <h5 className="modal-title" id="expenseModalLabel">
                                                        Expense Details
                                                    </h5>
                                                    <button
                                                        type="button"
                                                        className="btn-close"
                                                        data-bs-dismiss="modal"
                                                        aria-label="Close"
                                                    />
                                                </div>
                                                <div className="modal-body">
                                                    <table className="table table-striped mb-0">
                                                        <tbody>

                                                            <tr>
                                                                <td>From Date</td>
                                                                <td>{item.EXPENSE_FROM_DATE || item.EXPENSE_DATE || '-'}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>To Date</td>
                                                                <td>{item.EXPENSE_TO_DATE || item.EXPENSE_DATE || '-'}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>Total Amount</td>
                                                                <td> ${item.TOTAL_USD_EQUIV}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>Status</td>
                                                                <td>
                                                                    <span style={{ color: item.STATUS === 'Approved' ? 'green' : 'orange' }}>
                                                                        {item.STATUS}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td>Submitted By</td>
                                                                <td>{item.FULL_NAME}</td>
                                                            </tr>
                                                            <tr>
                                                                <td>Expense Items</td>
                                                                <td>
                                                                    <div className="table-responsive">
                                                                        <table className="table table-bordered table-sm align-middle mb-0">
                                                                            <thead className="table-light">
                                                                                <tr>
                                                                                    <th style={{ width: 50 }}>#</th>
                                                                                    <th>Type</th>
                                                                                    <th>Description</th>
                                                                                    <th>Amount</th>
                                                                                    <th>USD Equivalent</th>
                                                                                    <th style={{ width: 180 }}>Receipt</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {item.expense_items?.length > 0 ? item.expense_items.map((ei, idx) => {
                                                                                    const symbol = expenseCurrencySymbolMap[ei.CURRENCY] || ei.CURRENCY || '';
                                                                                    return (
                                                                                        <tr key={ei.ITEM_ID || idx}>
                                                                                            <td>{idx + 1}</td>
                                                                                            <td>{ei.EXPENSE_TYPE_NAME || '-'}</td>
                                                                                            <td>{ei.DESCRIPTION || '-'}</td>
                                                                                            <td>{symbol} {ei.AMOUNT || 0}</td>
                                                                                            <td>${ei.USD_EQUIV || 0}</td>
                                                                                            <td>
                                                                                                {ei.PROOF_DOCUMENT ? (
                                                                                                    <div className="d-flex gap-2 flex-wrap">
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="btn btn-info btn-sm"
                                                                                                            onClick={() => viewProofFile(ei.PROOF_DOCUMENT)}
                                                                                                        >
                                                                                                            View
                                                                                                        </button>
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            className="btn btn-success btn-sm"
                                                                                                            onClick={() => downloadProofFile(ei.PROOF_DOCUMENT)}
                                                                                                        >
                                                                                                            Download
                                                                                                        </button>
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <span className="text-muted">No receipt</span>
                                                                                                )}
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                }) : (
                                                                                    <tr>
                                                                                        <td colSpan="6" className="text-center text-muted">
                                                                                            No expense items available
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div className="modal-footer">
                                                    <button
                                                        type="button"
                                                        className="btn btn-light"
                                                        data-bs-dismiss="modal"
                                                    >
                                                        Close
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>

                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center text-muted">
                                    No submitted expenses found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>


            </div>


            {/* Preview Modal */}
            <div
                className="modal fade"
                id="previewModal"
                tabIndex="-1"
                aria-labelledby="previewModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Expense Report Preview</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            {previewUrl ? (
                                <iframe
                                    src={previewUrl}
                                    title="Expense Report Preview"
                                    width="100%"
                                    height="600px"
                                    style={{ border: "none" }}
                                ></iframe>
                            ) : (
                                <p className="text-center text-muted">Loading preview...</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="modal fade"
                id="receiptPreviewModal"
                tabIndex="-1"
                aria-labelledby="receiptPreviewModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-xl modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="receiptPreviewModalLabel">Receipt Preview</h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            {receiptPreview.error ? (
                                <p className="text-center text-danger mb-0">{receiptPreview.error}</p>
                            ) : canPreviewPdf ? (
                                <iframe
                                    src={receiptPreview.url}
                                    title="Receipt Preview"
                                    width="100%"
                                    height="600px"
                                    style={{ border: "none" }}
                                />
                            ) : canPreviewImage ? (
                                <img
                                    src={receiptPreview.url}
                                    alt="Receipt Preview"
                                    style={{ maxWidth: "100%", maxHeight: "70vh", display: "block", margin: "0 auto" }}
                                />
                            ) : receiptPreview.url ? (
                                <p className="text-center text-muted mb-0">
                                    Preview is not available for this file type. Please download the receipt.
                                </p>
                            ) : (
                                <p className="text-center text-muted mb-0">Loading receipt preview...</p>
                            )}
                        </div>
                        <div className="modal-footer">
                            {receiptPreview.fileName ? (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => downloadProofFile(receiptPreview.fileName)}
                                >
                                    Download Receipt
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="btn btn-light"
                                data-bs-dismiss="modal"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Paginations
                range={5}
                limit={limit}
                totalCount={total}
                currentPage={page}
                onPageChange={setCurrentPage}
            />
        </React.Fragment>

    );
};

export default SubmittedExpensesList;
